import type { ScreenView, TraceEvent } from "@typedoc/contracts/capture";
import type { PackageTraceLine } from "@typedoc/contracts/recordings/package";
import type { QuestionSeed, SessionEvent, TapeEntry } from "@typedoc/interview/events";

/**
 * The sample's finished recording on the Brightline chat (ENG-1015, filled out for the package
 * timeline in ENG-1028): six minutes of chasing a missing PO, with Abacor's voice. Six screens,
 * one flagged and one with a card field covered; a trace of apps, pages and actions; a stretch in
 * Slack, which was not shared; a pause; a question answered by voice and one held for the chat.
 *
 * Times are seconds from Start; the recording was made six seconds before it, while the person
 * chose what Abacor sees. The screens' images are drawings bundled with the web app
 * (`/design/sample-screens/`), standing in for Xero, the supplier portal and Mail.
 */

export const SAMPLE_FIRST: QuestionSeed = {
  id: "q-first",
  text: "Who do you chase first, the buyer or their accounts payable team?",
  urgency: "later",
  origin: "chat",
  evidence: "You asked Abacor to chase Brightline for a PO",
  likely: ["The buyer", "Accounts payable"],
};
export const SAMPLE_WAIT: QuestionSeed = {
  id: "q-wait",
  text: "How long do you wait before a second reminder?",
  urgency: "later",
  origin: "chat",
  evidence: "It has been six days since INV-2291",
  likely: ["Three working days", "A week"],
};

const XERO = { app: "Xero", window: "INV-2291 · Brightline Print" };
const PORTAL = { app: "Google Chrome", window: "Brightline supplier portal" };
const MAIL = { app: "Mail", window: "New message" };
const ORDERS = "https://portal.brightline.example/orders";

/** The kept screens: id, seconds, why, where, picture, and what the device did to it. */
const SCREENS: readonly (Pick<ScreenView, "id" | "trigger" | "context" | "covered" | "checked"> & {
  seconds: number;
  image: string;
})[] = [
  {
    id: "rscr_01m60sample0000000000xero1",
    seconds: 4,
    trigger: "context",
    context: XERO,
    covered: [],
    checked: true,
    image: "invoice.svg",
  },
  {
    id: "rscr_01m60sample00000000portal1",
    seconds: 47,
    trigger: "context",
    context: { ...PORTAL, url: ORDERS },
    covered: [],
    checked: true,
    image: "portal-orders.svg",
  },
  // Its fields could not be checked in time: kept, and flagged (D5).
  {
    id: "rscr_01m60sample00000000portal2",
    seconds: 82,
    trigger: "speech",
    context: { ...PORTAL, window: "Orders · INV-2291", url: `${ORDERS}?q=INV-2291` },
    covered: [],
    checked: false,
    image: "portal-results.svg",
  },
  {
    id: "rscr_01m60sample0000000000mail1",
    seconds: 158,
    trigger: "click",
    context: MAIL,
    covered: [],
    checked: true,
    image: "mail.svg",
  },
  // The card number was covered on the Mac before the screen left it.
  {
    id: "rscr_01m60sample00000000payment",
    seconds: 236,
    trigger: "covered",
    context: {
      ...PORTAL,
      window: "Payment details",
      url: "https://portal.brightline.example/account/payment",
    },
    covered: [{ x: 0.3528, y: 0.4978, width: 0.3472, height: 0.0489 }],
    checked: true,
    image: "portal-payment.svg",
  },
  {
    id: "rscr_01m60sample0000000000xero2",
    seconds: 300,
    trigger: "idle",
    context: XERO,
    covered: [],
    checked: true,
    image: "invoice.svg",
  },
];

const screenId = (index: number) => SCREENS[index]?.id ?? null;

/** What the person did and where, seconds from Start, and the screen kept last before it. */
const TRACE: readonly [seconds: number, event: TraceEvent, screen: number | null][] = [
  [1, { type: "app", ...XERO }, null],
  [20, { type: "field", role: "AXTextField", label: "Reference", kind: "reference" }, 0],
  [
    32,
    { type: "click", button: "left", label: "Copy reference", role: "AXButton", x: 0.71, y: 0.24 },
    0,
  ],
  [33, { type: "copy" }, 0],
  [45, { type: "app", ...PORTAL }, 0],
  [46, { type: "page", address: ORDERS }, 0],
  [60, { type: "field", role: "AXSearchField", label: "Search orders", kind: "reference" }, 1],
  [67, { type: "click", button: "left", label: "Search", role: "AXButton", x: 0.62, y: 0.2 }, 1],
  [70, { type: "page", address: `${ORDERS}?q=INV-2291` }, 1],
  [
    88,
    {
      type: "click",
      button: "left",
      label: "PO pending · INV-2291",
      role: "AXRow",
      x: 0.4,
      y: 0.42,
    },
    2,
  ],
  [118, { type: "app", ...MAIL }, 2],
  [125, { type: "field", role: "AXTextField", label: "To", kind: "email" }, 2],
  [135, { type: "field", role: "AXTextField", label: "Subject", kind: "text" }, 2],
  [146, { type: "download", name: "INV-2291.pdf", mediaType: "application/pdf" }, 2],
  [160, { type: "click", button: "left", label: "Send", role: "AXButton", x: 0.9, y: 0.08 }, 3],
  [220, { type: "app", ...PORTAL, window: "Payment details" }, 3],
  [221, { type: "page", address: "https://portal.brightline.example/account/payment" }, 3],
  [236, { type: "field", role: "AXTextField", label: "Card number", kind: "secret" }, 4],
  [252, { type: "field", role: "AXTextField", label: "Name on card", kind: "text" }, 4],
  [298, { type: "app", ...XERO }, 4],
  [312, { type: "click", button: "left", label: "Add note", role: "AXButton", x: 0.8, y: 0.31 }, 5],
  [318, { type: "field", role: "AXTextArea", label: "Note", kind: "text" }, 5],
];

/** The tape, seconds from Start; Start itself is the second entry. */
const TAPE: readonly [seconds: number, key: string, event: SessionEvent][] = [
  [
    -6,
    "start",
    {
      type: "session.started",
      intent: "Chase a missing PO",
      voice: "live",
      questions: [SAMPLE_WAIT, SAMPLE_FIRST],
    },
  ],
  [0, "person:start", { type: "person.start" }],
  [
    12,
    "voice:1",
    {
      type: "voice.heard",
      text: "This one's six days past due, so it's time to chase Brightline.",
      startMs: 0,
      endMs: 3_100,
    },
  ],
  [
    14,
    "judgment:1",
    {
      type: "match.none",
      input: 3,
      basedOn: 3,
      note: { topic: "When it starts", text: "Chases an invoice once it is six days past due" },
    },
  ],
  [
    40,
    "voice:2",
    {
      type: "voice.heard",
      text: "I copy the reference and look the PO up in their portal.",
      startMs: 0,
      endMs: 2_900,
    },
  ],
  [
    42,
    "judgment:2",
    {
      type: "match.none",
      input: 5,
      basedOn: 5,
      note: {
        topic: "Where it looks",
        text: "Looks the PO up in the Brightline portal by the invoice reference",
      },
    },
  ],
  [
    80,
    "voice:3",
    {
      type: "voice.heard",
      text: "There it is. No PO number on their side yet.",
      startMs: 0,
      endMs: 2_400,
    },
  ],
  [90, "voice:break-1", { type: "break" }],
  [96, "capture:event-1", { type: "screen.unshared", app: "Slack" }],
  [104, "capture:event-2", { type: "screen.unshared", app: null }],
  [
    110,
    "voice:4",
    {
      type: "voice.heard",
      text: "The buyer first. Accounts payable only if the buyer hasn't answered in three days.",
      startMs: 0,
      endMs: 4_600,
    },
  ],
  [
    112,
    "judgment:3",
    {
      type: "match.one",
      input: 11,
      basedOn: 11,
      questionId: "q-first",
      took: "Chases the buyer first, then accounts payable after three days",
    },
  ],
  [
    150,
    "voice:5",
    {
      type: "voice.heard",
      text: "Short and polite, with the invoice attached.",
      startMs: 0,
      endMs: 2_200,
    },
  ],
  [175, "person:pause", { type: "person.pause" }],
  [215, "person:resume", { type: "person.resume" }],
  [236, "capture:event-3", { type: "screen.masked", masked: true }],
  [252, "capture:event-4", { type: "screen.masked", masked: false }],
  [
    260,
    "voice:6",
    {
      type: "voice.heard",
      text: "I check they still have our card on file. I never read it out.",
      startMs: 0,
      endMs: 3_300,
    },
  ],
  [270, "voice:break-2", { type: "break" }],
  [
    322,
    "voice:7",
    {
      type: "voice.heard",
      text: "And a note on the invoice, so the next person knows it was chased.",
      startMs: 0,
      endMs: 3_400,
    },
  ],
  [340, "person:finish", { type: "person.finish" }],
];

export function sampleRecordingTape(startedAt: number): TapeEntry[] {
  return TAPE.map(([seconds, key, event], index) => ({
    seq: index + 1,
    key,
    at: startedAt + seconds * 1000,
    event,
  }));
}

/**
 * The sample's screens. They have no thumbnails, as a recording kept before thumbnails has none:
 * the package draws them small from their images, which are light drawings.
 */
export function sampleRecordingScreens(
  startedAt: number,
): { screen: ScreenView; url: string; thumbnailUrl: null }[] {
  return SCREENS.map(({ seconds, image, ...screen }, index) => ({
    screen: {
      ...screen,
      fileId: `file_01m60sample000000000screen${String(index)}`,
      thumbnailFileId: null,
      at: startedAt + seconds * 1000,
      width: 1440,
      height: 900,
    },
    url: `/design/sample-screens/${image}`,
    thumbnailUrl: null,
  }));
}

export function sampleRecordingTrace(startedAt: number): PackageTraceLine[] {
  return TRACE.map(([seconds, event, screen], index) => ({
    id: `rtrc_01m60sample0000000000trace${String(index).padStart(2, "0")}`,
    key: `trace-${String(startedAt + seconds * 1000)}-${String(index).padStart(6, "0")}`,
    at: startedAt + seconds * 1000,
    event,
    screenId: screen === null ? null : screenId(screen),
  }));
}
